<template>
  <form
    class="search-bar"
    @submit.prevent="handleFormSubmit"
  >
    <div class="search-bar__container">
      <input
        ref="searchInput"
        v-model="input"
        class="search-bar__input text-body-m text-body-s-desktop"
        :placeholder="$string('general.site_header.search.placeholder')"
        type="text"
        required
        minlength="2"
        @focus="handleFocus"
        @blur="handleBlur($event)"
        @input="clearValidationError"
        @invalid="setValidationError"
      >

      <button
        v-if="input"
        class="search-bar__clear"
        type="button"
        :aria-label="$string('search.clear_search')"
        @mousedown.prevent="clearInput"
      >
        <span
          class="visually-hidden"
          v-text="$string('search.clear_search')"
        />
        <close-svg />
      </button>

      <button
        class="search-bar__submit"
        type="submit"
        @mousedown.prevent="handleSubmitButton"
        @touchend.prevent="handleSubmitButton"
      >
        <span
          class="visually-hidden"
          v-text="$string('general.site_header.search.submit')"
        />

        <icon-search />
      </button>
      <span
        v-if="isMobile && isFocused"
        class="search-bar__divider"
      />
      <button
        v-if="isMobile && isFocused"
        class="search-bar__close"
        type="button"
        @mousedown.prevent="toggleClose"
      >
        <span
          class="visually-hidden"
          v-text="$string('general.site_header.search.close')"
        />
        <close-svg />
      </button>
    </div>
  </form>
</template>

<script>

/**
* Vue: Search bar (search-bar)
* -----------------------------------------------------------------------------
* Used in the header.
* - Acts as a small placeholder to only load predictive search when required.
*
* @param {Object} redirect - Nosto redirect URL.
* @param {String} searchQuery - Current search query, used to update input
* value when clicking suggestion.
*
*/
import { mapState } from 'vuex'

import IconSearch from '~icons/misc/search.svg'
import CloseSvg from '~icons/misc/close.svg'

export default {
  name: 'SearchBar',

  components: {
    IconSearch,
    CloseSvg,
  },

  props: {
    redirect: {
      type: String,
      default: '',
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-focus': (payload) => typeof payload === 'boolean',
    'search-input': (payload) => typeof payload === 'string',
    'close-search': null,
  },

  data() {
    return {
      input: '',
      query: '',
      timeout: '',
      isFocused: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      tabbable: (state) => state.accessibility.tabbable,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute form submit URL.
     * - Use special URL in redirect to make redirect URL relative.
     * @returns {String}
     */
    submitUrl() {
      const formattedRedirect = this.redirect.replace(
        'https://childsplayredirect.com/',
        this.$variable('routes.normalisedRoot'),
      )

      return this.redirect
        ? formattedRedirect
        : `${this.$variable('routes.search')}?q=${this.input}`
    },
  },

  watch: {

    /**
     * Watch input to debounce update query.
     * - Debounces input to set query only when finished typing.
     * - Don't watch query to emit event because search query can update query
     *   when user clicks on a suggestion, don't want to trigger two updates.
     */
    input(query) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        const normalisedQuery = this.normaliseAutocompleteQuery(query)

        this.query = normalisedQuery
        this.$emit('search-input', normalisedQuery)
      }, 10)
    },

    /**
     * Watch search query to update query.
     */
    searchQuery() {
      this.input = this.searchQuery
    },
  },

  methods: {

    /**
     * Handle focus to emit event.
     */
    handleFocus() {
      this.isFocused = true
      this.$emit('search-focus', true)
    },

    /**
     * Handle blur event on the search input.
     * - Checks if focus is moving to related elements (submit/close buttons).
     * - Only removes focus state if not moving to these related elements.
     * @param {FocusEvent} event - The blur event
     */
    handleBlur(event) {
      // Only blur if not moving to submit or close button
      const next = event.relatedTarget

      if (
        next &&
        (next.classList.contains('search-bar__submit') || next.classList.contains('search-bar__close'))
      ) {
        return
      }

      this.isFocused = false
    },

    /**
     * Handle button blur.
     * - Assumes that if tabbable is active, there is no query, and button blurs
     *   then the user wants to tab outside the search bar so predictive search
     *   should close.
     */
    handleButtonBlur() {
      if (!this.tabbable || this.query) {
        return
      }

      this.$emit('search-focus', false)
    },

    /**
     * Handle form submit.
     * - Requires at least 2 non-whitespace characters to prevent empty/space-only searches.
     */
    handleFormSubmit() {
      if (!this.$refs.searchInput.reportValidity()) {
        return
      }

      location.href = this.submitUrl
    },

    /**
     * Set the native validation message for short search terms.
     * @param {Event} event - Invalid input event.
     */
    setValidationError(event) {
      event.target.setCustomValidity('Please enter at least 2 characters to search.')
    },

    /**
     * Clear the native validation message while the user types.
     * @param {Event} event - Input event.
     */
    clearValidationError(event) {
      event.target.setCustomValidity('')
    },

    /**
     * Clear the search input field.
     * - Resets both input and query state variables.
     * - Emits empty search-input event to notify parent components.
     */
    clearInput() {
      this.input = ''
      this.query = ''
      this.$emit('search-input', '')

    },

    /**
     * Normalise autocomplete query input.
     * - Removes hyphens so "t-shirt" can match terms like "tshirt".
     * - Collapses whitespace and trims leading/trailing spaces.
     * @param {String} value - Raw input value.
     * @returns {String}
     */
    normaliseAutocompleteQuery(value) {
      return String(value || '')
        .replace(/-/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },

    /**
     * Close the search component.
     * - Removes focus state.
     * - Removes focus from the input element.
     * - Emits close-search event to notify parent components.
     */
    toggleClose() {
      this.isFocused = false

      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur()
      }

      this.$emit('close-search')
    },

    /**
     * Handles the submit button interaction for the search bar.
     * On iOS devices, the first tap focuses the input (without submitting),
     * and the second tap submits the form if the input is already focused.
     * This ensures consistent behavior across all devices.
     */
    handleSubmitButton() {
      if (!this.$refs.searchInput.reportValidity()) {
        return
      }

      if (document.activeElement !== this.$refs.searchInput) {
        this.$refs.searchInput.focus()
        return
      }

      this.handleFormSubmit()
    },
  },
}
</script>

<style lang="scss">
@import './search-bar';
</style>
